import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { springs } from '@/lib/motion';
import { applyTheme } from '@/lib/theme';
import { Button } from '@/ui/Button';
import { Shape } from '@/ui/Shape';

export function LandingScreen() {
  const nav = useNavigate();
  const [pin, setPin] = useState('');

  useEffect(() => {
    applyTheme('default');
  }, []);

  return (
    <div className="min-h-screen px-6 py-10 md:py-20 relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <Shape
          kind="triangle"
          size={220}
          className="absolute top-12 left-8 text-a1 opacity-20 animate-wobble"
        />
        <Shape
          kind="diamond"
          size={180}
          className="absolute top-32 right-16 text-a3 opacity-20 animate-floaty"
        />
        <Shape
          kind="circle"
          size={160}
          className="absolute bottom-20 left-1/4 text-a2 opacity-20 animate-floaty"
        />
        <Shape
          kind="square"
          size={200}
          className="absolute bottom-10 right-10 text-a4 opacity-20 animate-wobble"
        />
      </div>

      <div className="max-w-3xl mx-auto relative z-10">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={springs.gentle}
          className="text-center mb-12"
        >
          <span className="sticker bg-accent text-paper mb-4 inline-block">
            family · party · quiz
          </span>
          <h1 className="display font-display font-black text-6xl md:text-8xl text-ink">
            A live trivia
            <br />
            <span className="text-accent">for people who actually</span>
            <br />
            know each other.
          </h1>
          <p className="mt-6 text-xl text-muted max-w-xl mx-auto">
            Write the quiz. Or invite your aunts and cousins to write it. Then gather round the TV
            and play.
          </p>
        </motion.div>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ ...springs.bouncy, delay: 0.15 }}
          className="chunky-lg bg-paper p-6 md:p-8 mb-6"
        >
          <div className="text-center mb-4">
            <span className="font-display font-bold text-sm uppercase tracking-wider text-muted">
              Got a PIN?
            </span>
          </div>
          <div className="flex flex-col md:flex-row items-stretch gap-3">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="6-digit PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              className="ink-border-thick rounded-chunk bg-canvas px-6 py-5 display font-display font-black text-4xl text-center tabular flex-1 outline-none focus:ring-4 focus:ring-accent/40"
            />
            <Button
              variant="accent"
              size="xl"
              disabled={pin.length !== 6}
              onClick={() => nav(`/play?pin=${pin}`)}
            >
              Join
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ ...springs.bouncy, delay: 0.3 }}
          className="grid md:grid-cols-2 gap-4"
        >
          <Link
            to="/host"
            className="chunky bg-paper p-6 hover:bg-canvas/30 transition-colors block group"
          >
            <Shape
              kind="diamond"
              size={48}
              className="text-a2 mb-3 group-hover:rotate-12 transition-transform"
            />
            <h3 className="display font-display font-bold text-2xl mb-1">I'm hosting</h3>
            <p className="text-muted">Build a quiz, invite contributors, run the game on the TV.</p>
          </Link>

          <Link
            to="/play"
            className="chunky bg-paper p-6 hover:bg-canvas/30 transition-colors block group"
          >
            <Shape
              kind="circle"
              size={48}
              className="text-a3 mb-3 group-hover:scale-110 transition-transform"
            />
            <h3 className="display font-display font-bold text-2xl mb-1">I'm joining</h3>
            <p className="text-muted">Got a PIN or a join link? This way.</p>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
